import React from 'react';
import { Link } from 'react-router-dom';
import { Clock, ChevronRight, Sparkles, Package } from 'lucide-react';
import { getImageUrl } from '../../utils/imageUtils';
import { formatINR } from '../../utils/currency';

export function dealProductImage(product: { images?: unknown[] } | undefined): string {
  const img = product?.images?.[0];
  if (!img) return '/placeholder-product.jpg';
  const url = typeof img === 'string' ? img : (img as { url?: string })?.url;
  return getImageUrl(url || '');
}

export function dealBannerUrl(banner?: string): string {
  if (!banner) return '';
  return getImageUrl(banner);
}

export const CountdownTimer: React.FC<{ endDate: string; compact?: boolean }> = ({
  endDate,
  compact,
}) => {
  const [timeLeft, setTimeLeft] = React.useState({ hours: 0, minutes: 0, seconds: 0 });

  React.useEffect(() => {
    const tick = () => {
      const diff = new Date(endDate).getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setTimeLeft({
        hours: Math.floor(diff / (1000 * 60 * 60)),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [endDate]);

  const box = (value: number, label: string) => (
    <div
      className={`bg-white/95 backdrop-blur rounded-lg text-center shadow-sm border border-white/40 ${
        compact ? 'min-w-[52px] px-2 py-1.5' : 'min-w-[64px] px-3 py-2'
      }`}
    >
      <div className={`font-bold text-slate-900 tabular-nums ${compact ? 'text-lg' : 'text-xl'}`}>
        {String(value).padStart(2, '0')}
      </div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">{label}</div>
    </div>
  );

  return (
    <div className="flex items-center gap-2">
      {box(timeLeft.hours, 'Hrs')}
      <span className="text-white/80 font-bold text-lg">:</span>
      {box(timeLeft.minutes, 'Min')}
      <span className="text-white/80 font-bold text-lg">:</span>
      {box(timeLeft.seconds, 'Sec')}
    </div>
  );
};

export const DealProductCard: React.FC<{
  slug: string;
  name: string;
  image: string;
  dealPrice: number;
  originalPrice: number;
  discountPercent: number;
  layout?: 'grid' | 'slider';
}> = ({ slug, name, image, dealPrice, originalPrice, discountPercent, layout = 'grid' }) => {
  const savings = Math.max(0, originalPrice - dealPrice);

  return (
    <Link
      to={`/product/${slug}`}
      className={`group flex flex-col bg-white rounded-2xl border border-slate-200/80 overflow-hidden hover:border-orange-200 hover:shadow-lg hover:shadow-orange-100/50 transition-all duration-300 h-full ${
        layout === 'slider' ? 'w-[168px] sm:w-[200px] md:w-[220px] flex-shrink-0 snap-start' : ''
      }`}
    >
      <div className="relative aspect-square max-h-[200px] sm:max-h-[220px] w-full bg-gradient-to-b from-slate-50 to-white flex items-center justify-center p-3 sm:p-4">
        <img
          src={image}
          alt={name}
          className="max-h-full max-w-full object-contain group-hover:scale-[1.03] transition-transform duration-300"
          loading="lazy"
        />
        {discountPercent > 0 && (
          <span className="absolute top-2.5 left-2.5 bg-gradient-to-r from-orange-500 to-red-500 text-white text-[11px] font-bold px-2 py-1 rounded-md shadow-sm">
            {discountPercent}% OFF
          </span>
        )}
      </div>
      <div className="flex flex-col flex-1 p-3 sm:p-4 pt-2 border-t border-slate-100">
        <h3 className="text-sm sm:text-[15px] font-semibold text-slate-800 leading-snug line-clamp-2 min-h-[2.5rem] group-hover:text-orange-600 transition-colors">
          {name}
        </h3>
        <div className="mt-auto pt-3 space-y-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-lg sm:text-xl font-bold text-slate-900">{formatINR(dealPrice)}</span>
            {originalPrice > dealPrice && (
              <span className="text-xs sm:text-sm text-slate-400 line-through">{formatINR(originalPrice)}</span>
            )}
          </div>
          {savings > 0 && (
            <p className="text-xs font-medium text-emerald-600">You save {formatINR(savings)}</p>
          )}
        </div>
      </div>
    </Link>
  );
};

export const DealsBreadcrumb: React.FC<{ current?: string }> = ({ current }) => (
  <nav className="flex items-center gap-2 text-sm text-slate-500 mb-4">
    <Link to="/" className="hover:text-orange-600 transition-colors">
      Home
    </Link>
    <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
    <Link to="/deals" className="hover:text-orange-600 transition-colors">
      Deals
    </Link>
    {current && (
      <>
        <ChevronRight className="w-3.5 h-3.5 text-slate-400" />
        <span className="text-slate-800 font-medium truncate max-w-[200px] sm:max-w-none">
          {current}
        </span>
      </>
    )}
  </nav>
);

export const DealsPageHero: React.FC<{ subtitle?: string }> = ({ subtitle }) => (
  <div className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-gradient-to-br from-slate-900 via-red-900 to-orange-700 text-white px-6 py-10 sm:px-10 sm:py-14 mb-8 sm:mb-10 shadow-xl">
    <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_30%_20%,white,transparent_50%)]" />
    <div className="relative max-w-3xl">
      <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider mb-4">
        <Sparkles className="w-3.5 h-3.5" />
        Limited time offers
      </div>
      <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">Deals &amp; Offers</h1>
      <p className="mt-3 text-base sm:text-lg text-white/85 leading-relaxed">
        {subtitle || 'Hand-picked discounts on top products. Prices update automatically while the deal lasts.'}
      </p>
    </div>
  </div>
);

export const SectionHeader: React.FC<{ title: string; count: number; layout: string }> = ({
  title,
  count,
  layout,
}) => (
  <div className="flex items-end justify-between gap-4 mb-5 sm:mb-6 pb-3 border-b border-slate-200">
    <div>
      <h2 className="text-xl sm:text-2xl font-bold text-slate-900">{title}</h2>
      <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-1.5">
        <Package className="w-4 h-4" />
        {count} {count === 1 ? 'product' : 'products'}
        <span className="text-slate-300">·</span>
        <span className="capitalize">{layout === 'slider' ? 'Scroll row' : 'Grid'}</span>
      </p>
    </div>
  </div>
);

export const LoadingDealsGrid: React.FC = () => (
  <div className="grid gap-5 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
    {[1, 2, 3].map((i) => (
      <div key={i} className="bg-white rounded-2xl border border-slate-100 overflow-hidden animate-pulse">
        <div className="aspect-[16/9] bg-slate-200" />
        <div className="p-5 space-y-3">
          <div className="h-5 bg-slate-200 rounded w-3/4" />
          <div className="h-4 bg-slate-100 rounded w-1/2" />
        </div>
      </div>
    ))}
  </div>
);

export const DealEndsBadge: React.FC<{ endDate: string }> = ({ endDate }) => (
  <span className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-slate-600 bg-slate-50 border border-slate-100 px-2.5 py-1 rounded-lg">
    <Clock className="w-3.5 h-3.5 text-orange-500" />
    Ends {new Date(endDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
  </span>
);
